import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useFormatters } from '../../../hooks';
import {
  Timeline,
  TimelineItem,
  TimelineDot,
  TimelineContent,
  TimelineOppositeContent,
  TimelineConnector,
  TimelineSeparator,
  Typography,
  Box,
} from '../../../ui/core';
import { getTripDriverHistory } from '../../../redux/Trips/actions';
import { RideStatus } from '../../../models/Rides';

const UserLink = ({ user, defaultLabel }) => {
  if (!user) {
    return <span>{defaultLabel}</span>;
  }

  return (
    <Box component="a" href={`/users/${user.id}/`} target="_blank" rel="noreferrer" sx={{ color: 'accent.main' }}>
      {user.firstName} {user.lastName}
    </Box>
  );
};

const DriverLink = ({ driver }) => <UserLink user={driver} defaultLabel="Driver" />;

const RiderLink = ({ rider }) => <UserLink user={rider} defaultLabel="Rider" />;

// Helper: calculate distance between two coordinates in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
};

// Helper: render driver arrival status component
const renderDriverArrivalStatus = (driverArrivalStatus) => {
  if (!driverArrivalStatus) return null;
  
  if (driverArrivalStatus.status === 'unknown') {
    return (
      <Box component="span" sx={{ color: 'text.secondary', ml: '8px' }}>
        (Driver arrival unknown)
      </Box>
    );
  }
  
  if (driverArrivalStatus.status === 'on_time') {
    return null; // Don't show anything if driver is on time
  }
  
  return (
    <Box component="span" sx={{ color: driverArrivalStatus.color, ml: '8px' }}>
      ({driverArrivalStatus.label})
    </Box>
  );
};

// Helper: get drop-off location info component
const getDropoffLocationInfo = (action, ride, locationData) => {
  if (!locationData.length || !action.timestamp) return null;
  
  const targetMs = Date.parse(action.timestamp);
  const twoMinutesMs = 2 * 60 * 1000; // 2 minutes in milliseconds
  
  // Find all points within 2 minutes
  const nearbyPoints = locationData.filter(point => {
    const pointMs = Date.parse(point.timestamp);
    const timeDiff = Math.abs(pointMs - targetMs);
    return timeDiff <= twoMinutesMs;
  });
  
  if (nearbyPoints.length === 0) return null;
  
  // Find the closest point by time
  const locationPoint = nearbyPoints.reduce((closest, current) => {
    const closestDiff = Math.abs(Date.parse(closest.timestamp) - targetMs);
    const currentDiff = Math.abs(Date.parse(current.timestamp) - targetMs);
    return currentDiff < closestDiff ? current : closest;
  });
  
  // Calculate distance if we have scheduled coordinates
  if (ride.dropOff?.coords?.coordinates) {
    const scheduledCoords = ride.dropOff.coords.coordinates;
    const distance = calculateDistance(
      locationPoint.coordinate.lat,
      locationPoint.coordinate.lon,
      scheduledCoords[1], // lat
      scheduledCoords[0]  // lon
    );
    
    const locationTime = new Date(locationPoint.timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    const color = distance <= 0.2 ? 'success.main' : distance < 1 ? 'warning.main' : 'error.main';
    
    return (
      <>
        <Box component="span" sx={{ color: 'text.inactive', ml: '8px' }}>•</Box>
        <Box component="span" sx={{ color, ml: '8px' }}>
          {distance.toFixed(1)} mi away
        </Box>
        {distance > 0.2 && (
          <Box component="span" sx={{ color: 'text.inactive', fontStyle: 'italic', ml: '8px' }}>
            {locationTime}
          </Box>
        )}
      </>
    );
  }
  
  return null;
};

const TripActivity = ({ trip, locationData = [], fullTripData }) => {
  const f = useFormatters();
  const dispatch = useDispatch();

  const driverHistory = trip.driverHistory || [];
  const detailedDriverHistory = useSelector((state) => state.trips.tripDriversHistory[trip.id] || []);

  useEffect(() => {
    getTripDriverHistory(trip.id)(dispatch);
  }, [trip.id, dispatch, driverHistory.length]);

  const timeline = [];

  // Helper: compute on-time status for pickup or drop-off actions
  const getOnTimeStatus = (ride, action) => {
    const isPickup = action.status === RideStatus.en_route;
    
    // HITCH-4018: Don't show status for drop-off delays finding driver, since they will be
    // incorrect. Ops adjustments don't have this problem — they shift departureTime and
    // arrivalTime by the same delta, so the scheduled drop-off stays coherent — so only
    // non-ops history entries (including legacy entries with no `reason`) trigger this.
    if (!isPickup && ride.departureTimeHistory?.some((h) => h.reason !== 'ops_adjustment')) {
      return (
        <Box component="span" sx={{ color: 'text.secondary' }}> Delay unknown</Box>
      );
    }
    
    let scheduledTimeIso;
    if (isPickup) {
      const history = ride.departureTimeHistory || [];
      scheduledTimeIso = (history[0] && history[0].pastDeptTime) 
        ? history[0].pastDeptTime 
        : ride.departureTime;
    } else {
      scheduledTimeIso = ride.arrivalTime;
    }
    
    if (!scheduledTimeIso || !action.timestamp) return null;

    const scheduledMs = Date.parse(scheduledTimeIso);
    const actualMs = Date.parse(action.timestamp);

    if (Number.isNaN(scheduledMs) || Number.isNaN(actualMs)) return null;

    const minutesLate = Math.round((actualMs - scheduledMs) / 60000);
    let label = ''
    if (isPickup) {
      label = minutesLate <= 0 ? `On time` : `Delayed ${minutesLate} minutes`
    } else {
      if (minutesLate < 0) {
        label = `Arrived ${Math.abs(minutesLate)} minutes early`
      } else if (minutesLate === 0) {
        label = `Arrived on time`
      } else {
        label = `Arrived ${minutesLate} minutes late`
      }
    }
    const color = minutesLate <= 0 ? 'success.main' : minutesLate <= 15 ? 'warning.main' : 'error.main';

    return (
      <Box component="span" sx={{ color }}>{` ${label}`}</Box>
    );
  };

  // add driver accepted / cancelled history to timeline
  detailedDriverHistory.forEach((item) => {
    if (item.acceptedAt) {
      timeline.push({
        key: Date.parse(item.acceptedAt),
        time: f.date.toTimezoneLocaleString(
          item.acceptedAt,
          trip.originCity.timeZone,
          f.date.DateFormats.DATETIME_SIMPLE_WITH_TIMEZONE,
        ),
        action: (
          <Typography>
            <DriverLink driver={item.driver} /> accepted
          </Typography>
        ),
      });
    }
    if (item.cancelledAt) {
      timeline.push({
        key: Date.parse(item.cancelledAt),
        time: f.date.toTimezoneLocaleString(
          item.cancelledAt,
          trip.originCity.timeZone,
          f.date.DateFormats.DATETIME_SIMPLE_WITH_TIMEZONE,
        ),
        action: (
          <Typography>
            <DriverLink driver={item.driver} /> cancelled ({item.reason})
          </Typography>
        ),
      });
    }
  });
  if (trip.acceptedAt && trip.driver) {
    timeline.push({
      key: Date.parse(trip.acceptedAt),
      time: f.date.toTimezoneLocaleString(
        trip.acceptedAt,
        trip.originCity.timeZone,
        f.date.DateFormats.DATETIME_SIMPLE_WITH_TIMEZONE,
      ),
      action: (
        <Typography>
          <DriverLink driver={trip.driver} /> accepted
        </Typography>
      ),
    });
  }
  if (trip.confirmedAt && trip.driver) {
    timeline.push({
      key: Date.parse(trip.confirmedAt),
      time: f.date.toTimezoneLocaleString(
        trip.confirmedAt,
        trip.originCity.timeZone,
        f.date.DateFormats.DATETIME_SIMPLE_WITH_TIMEZONE,
      ),
      action: (
        <Typography>
          <DriverLink driver={trip.driver} /> confirmed
        </Typography>
      ),
    });
  }

  // add riders pick-up / drop-off history to timeline
  const rides = trip.rides;
  rides.forEach((ride) => {
    // Find matching ride in fullTripData.rides if it exists
    const enrichedRide = fullTripData?.rides?.find(fullRide => 
      fullRide.id === ride.id || fullRide._id === ride.id || fullRide._id === ride._id
    ) || ride;
    
    const actions = [
      ride.statusUpdates.find((u) => u.status === RideStatus.en_route),
      ride.statusUpdates.find((u) => u.status === RideStatus.completed),
    ];
    actions.forEach((action) => {
      if (!action) return;

      const isPickup = action.status === RideStatus.en_route;
      const onTimeStatus = getOnTimeStatus(ride, action);
      
             // Get driver arrival status for pickup actions
       const driverArrivalStatus = isPickup ? enrichedRide.pickupInfo?.driverArrivalStatus : null;
      
      // Get location info for drop-off actions
      const locationInfo = !isPickup ? getDropoffLocationInfo(action, ride, locationData) : null;

      timeline.push({
        key: Date.parse(action.timestamp),
        time: f.date.toTimezoneLocaleString(
          action.timestamp,
          trip.originCity.timeZone,
          f.date.DateFormats.DATETIME_SIMPLE_WITH_TIMEZONE,
        ),
        action: (
          <Typography>
            <DriverLink driver={trip.driver} /> marked <RiderLink rider={ride.rider} /> as{' '}
            <b>{isPickup ? 'picked-up' : 'dropped-off'}</b>
            {onTimeStatus && (
              <>
                <Box component="span" sx={{ color: 'text.inactive', ml: '8px' }}>•</Box>
                <Box component="span" sx={{ fontWeight: 'bold', ml: '8px' }}>{onTimeStatus}</Box>
              </>
            )}
            {renderDriverArrivalStatus(driverArrivalStatus)}
            {locationInfo}
          </Typography>
        ),
      });
    });
  });

  return (
    <Timeline>
      {timeline
        .sort((i1, i2) => i2.key - i1.key)
        .map((item, idx) => (
          <TimelineItem key={item.key}>
            <TimelineOppositeContent color="text.secondary" sx={{ flex: '0 1 auto' }}>
              {item.time}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              {idx !== timeline.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>{item.action}</TimelineContent>
          </TimelineItem>
        ))}
    </Timeline>
  );
};

export default TripActivity;
